import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, FadeUp } from "@/components/motion";
import EditorialMarquee from "@/components/EditorialMarquee";
import { IMAGES, ABOUT_LEAD, CHAPTERS } from "@/data/content";

const STATS = [
  { value: "01", label: "Global Hub", detail: "Headquartered in Dubai, UAE" },
  { value: "04+", label: "Core Products", detail: "Fuel Oil · MHO · Diesel · Crude Oil" },
  { value: "06", label: "Strategic Advantages", detail: "The Dubai Advantage, end to end" },
];

function StoryHero() {
  return (
    <section data-testid="about-hero" className="pt-44 pb-20 sm:pb-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeUp delay={0.1}>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">About AA GULF FZE</p>
        </FadeUp>
        <MaskedLines
          className="mt-8 font-display font-medium uppercase tracking-tighter leading-[0.95] text-[14vw] sm:text-[10vw] lg:text-[7.5rem] text-ink"
          delay={0.25}
          lines={[
            "Our",
            <span key="story">
              Story<span className="text-brand">.</span>
            </span>,
          ]}
        />
        <FadeUp delay={0.9} className="mt-10 max-w-xl">
          <p className="text-black/65 font-light leading-relaxed text-base sm:text-lg">{ABOUT_LEAD}</p>
        </FadeUp>
      </div>
    </section>
  );
}

function ParallaxBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="relative h-[60vh] sm:h-[75vh] overflow-hidden border-y border-black/10">
      <motion.div style={{ y }} className="absolute inset-0 scale-125">
        <img src={IMAGES.rigSunset} alt="Offshore energy platform" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-ink/35" />
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-8 flex items-end pb-12">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/85">
          Powering Industries · Economies · Lives
        </p>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section data-testid="about-manifesto" className="py-24 sm:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <div className="md:sticky md:top-32">
            <Reveal>
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">Who We Are</p>
            </Reveal>
          </div>
        </div>
        <div className="md:col-span-8 space-y-10">
          <Reveal>
            <p className="font-display text-2xl sm:text-4xl font-medium leading-snug tracking-tight text-ink">
              With a profound commitment to excellence and a deep understanding of the
              global energy landscape, we trade the products that power industries,
              economies, and lives<span className="text-brand">.</span>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-black/60 font-light leading-relaxed max-w-2xl">
              Dubai is more than our address — it is our advantage. From this crossroads
              of global commerce, AA GULF FZE connects producers, refiners and end users
              across continents, moving energy where it creates the most value.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-black/60 font-light leading-relaxed max-w-2xl">
              Every trade is built on the same foundations: disciplined sourcing,
              transparent terms and long-term partnerships. It is how a trading house
              earns its reputation — cargo by cargo, relationship by relationship.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Stats() {
  return (
    <section data-testid="about-stats" className="border-t border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid sm:grid-cols-3">
        {STATS.map((s, i) => (
          <div
            key={s.label}
            data-testid={`stat-${i}`}
            className={`py-14 sm:py-20 px-2 sm:px-8 ${i > 0 ? "border-t sm:border-t-0 sm:border-l border-black/10" : ""}`}
          >
            <Reveal delay={i * 0.1}>
              <p className="font-display font-medium text-6xl sm:text-7xl tracking-tighter text-brand">{s.value}</p>
              <p className="mt-4 font-mono text-[11px] tracking-[0.25em] uppercase text-ink">{s.label}</p>
              <p className="mt-3 text-black/50 font-light text-sm leading-relaxed">{s.detail}</p>
            </Reveal>
          </div>
        ))}
      </div>
    </section>
  );
}

function Advantages() {
  return (
    <section className="py-24 sm:py-32 border-t border-black/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <h2 className="font-display font-medium uppercase tracking-tight text-3xl sm:text-4xl text-ink">
            Built on six advantages<span className="text-brand">.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.05} className="bg-paper">
              <div data-testid={`about-chapter-${c.num}`} className="p-8 h-full group hover:bg-mist transition-colors duration-500">
                <p className="font-mono text-sm text-brand">{c.num}</p>
                <h3 className="mt-4 font-display text-xl font-medium tracking-tight text-ink">{c.title}</h3>
                <p className="mt-3 text-black/55 font-light text-sm leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function AboutCta() {
  return (
    <section className="relative overflow-hidden border-t border-black/10">
      <div className="absolute inset-0">
        <img src={IMAGES.handshake} alt="Business partnership" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-paper/85" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-28 sm:py-36">
        <Reveal>
          <h2 className="font-display font-medium uppercase tracking-tight text-4xl sm:text-6xl leading-[1.02] max-w-3xl text-ink">
            Let's build the next trade together<span className="text-brand">.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            to="/contact"
            data-testid="about-cta-contact"
            className="mt-10 group inline-flex items-center gap-2 bg-brand text-white px-8 py-4 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-brand-hover transition-colors duration-300"
          >
            Contact Us
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <StoryHero />
      <ParallaxBand />
      <Manifesto />
      <Stats />
      <Advantages />
      <EditorialMarquee />
      <AboutCta />
    </div>
  );
}
